<script>
// utils and composables
import { login } from '../../api/auth';
import { getLoginRedirectURL } from '../../helpers/AuthHelper';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { useBranding } from 'shared/composables/useBranding';
import AnalyticsHelper from 'dashboard/helper/AnalyticsHelper';
import { SESSION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

// components
import SimpleDivider from '../../components/Divider/SimpleDivider.vue';
import FormInput from '../../components/Form/Input.vue';
import GoogleOAuthButton from '../../components/GoogleOauth/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AuthLayout from '../../components/AuthLayout.vue';
import AuthHeading from '../../components/AuthHeading.vue';
import AuthButton from '../../components/AuthButton.vue';
import AuthShowcase from '../../components/AuthShowcase.vue';
import MfaVerification from 'dashboard/components/auth/MfaVerification.vue';
import SessionLimitOverlay from 'dashboard/components/auth/SessionLimitOverlay.vue';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
  'business-account-only': 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY',
  'saml-authentication-failed': 'LOGIN.SAML.API.ERROR_MESSAGE',
  'saml-not-enabled': 'LOGIN.SAML.API.ERROR_MESSAGE',
};

const IMPERSONATION_URL_SEARCH_KEY = 'impersonation';
const USER_NOT_CONFIRMED_ERROR_CODE = 'user_not_confirmed';
const AUTH_ERROR_TOAST_DURATION = 6000;

export default {
  components: {
    FormInput,
    GoogleOAuthButton,
    Spinner,
    SimpleDivider,
    AuthLayout,
    AuthHeading,
    AuthButton,
    AuthShowcase,
    MfaVerification,
    SessionLimitOverlay,
    Icon,
  },
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return {
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      // We need to initialize the component with any
      // properties that will be used in it
      credentials: {
        email: '',
        password: '',
      },
      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
      mfaRequired: false,
      mfaToken: null,
      verificationChannel: null,
      sessionsLimitReached: false,
      limitedSessions: [],
    };
  },
  validations() {
    return {
      credentials: {
        password: {
          required,
        },
        email: {
          required,
          email,
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    allowedLoginMethods() {
      return window.talkrobinConfig.allowedLoginMethods || ['email'];
    },
    showGoogleOAuth() {
      return (
        this.allowedLoginMethods.includes('google_oauth') &&
        Boolean(window.talkrobinConfig.googleOAuthClientId)
      );
    },
    showSignupLink() {
      return window.talkrobinConfig.signupEnabled === 'true';
    },
    showSamlLogin() {
      return this.allowedLoginMethods.includes('saml');
    },
  },
  created() {
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
  },
  mounted() {
    if (this.authError) {
      // Wait for the sibling snackbar to mount and subscribe to toast events.
      this.$nextTick(() => {
        const messageKey = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
        // Use a method to get the translated text to avoid dynamic key warning
        const translatedMessage = this.getTranslatedMessage(messageKey);
        useAlert(translatedMessage, { duration: AUTH_ERROR_TOAST_DURATION });
        // wait for idle state
        this.requestIdleCallbackPolyfill(() => {
          // Remove the error query param from the url
          const { query } = this.$route;
          this.$router.replace({ query: { ...query, error: undefined } });
        });
      });
    }
  },
  methods: {
    getTranslatedMessage(key) {
      // Avoid dynamic key warning by handling each case explicitly
      switch (key) {
        case 'LOGIN.OAUTH.NO_ACCOUNT_FOUND':
          return this.$t('LOGIN.OAUTH.NO_ACCOUNT_FOUND');
        case 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY':
          return this.$t('LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY');
        case 'LOGIN.API.UNAUTH':
        default:
          return this.$t('LOGIN.API.UNAUTH');
      }
    },
    // TODO: Remove this when Safari gets wider support
    // Ref: https://caniuse.com/requestidlecallback
    //
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        // Fallback for safari
        // Using a delay of 0 allows the callback to be executed asynchronously
        // in the next available event loop iteration, similar to requestIdleCallback
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message) {
      // Reset loading, current selected agent
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      useAlert(this.loginApi.message);
    },
    handleImpersonation() {
      // Detects impersonation mode via URL and sets a session flag to prevent user settings changes during impersonation.
      const urlParams = new URLSearchParams(window.location.search);
      const impersonation = urlParams.get(IMPERSONATION_URL_SEARCH_KEY);
      if (impersonation) {
        SessionStorage.set(SESSION_STORAGE_KEYS.IMPERSONATION_USER, true);
      }
    },
    submitLogin() {
      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;

      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
      };

      login(credentials)
        .then(result => {
          // Check if MFA is required
          if (result?.mfaRequired) {
            this.loginApi.showLoading = false;
            this.mfaRequired = true;
            this.mfaToken = result.mfaToken;
            this.verificationChannel = result.verificationChannel || null;
            return;
          }

          // Check if sessions limit reached
          if (result?.sessionsLimitReached) {
            this.loginApi.showLoading = false;
            this.sessionsLimitReached = true;
            this.limitedSessions = result.sessions;
            AnalyticsHelper.track(SESSION_EVENTS.LIMIT_HIT);
            return;
          }

          this.handleImpersonation();
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        })
        .catch(response => {
          if (response?.errorCode === USER_NOT_CONFIRMED_ERROR_CODE) {
            this.loginApi.showLoading = false;
            this.$router.push({
              name: 'auth_verify_email',
              state: { email: credentials.email },
            });
            return;
          }

          // Reset URL Params if the authentication is invalid
          if (this.email) {
            window.location = '/app/login';
          }
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    submitFormLogin() {
      if (this.v$.credentials.email.$invalid && !this.email) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }

      this.submitLogin();
    },
    handleMfaVerified(data) {
      // Verification successful; honor the requested account/conversation link
      // the same way the direct-login path does, instead of always going to /app.
      this.handleImpersonation();
      window.location = getLoginRedirectURL({
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
        user: data?.data,
      });
    },
    handleMfaCancel() {
      // User cancelled MFA, reset state
      this.mfaRequired = false;
      this.mfaToken = null;
      this.verificationChannel = null;
      this.credentials.password = '';
    },
    retryLoginWithParams(extraParams) {
      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
        ...extraParams,
      };

      this.sessionsLimitReached = false;
      this.limitedSessions = [];
      this.loginApi.showLoading = true;
      login(credentials)
        .then(result => {
          if (result?.mfaRequired) {
            this.loginApi.showLoading = false;
            this.mfaRequired = true;
            this.mfaToken = result.mfaToken;
            this.verificationChannel = result.verificationChannel || null;
            return;
          }
          if (result?.sessionsLimitReached) {
            this.loginApi.showLoading = false;
            this.sessionsLimitReached = true;
            this.limitedSessions = result.sessions;
            AnalyticsHelper.track(SESSION_EVENTS.LIMIT_HIT);
            return;
          }
          this.handleImpersonation();
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
        })
        .catch(response => {
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    handleSessionRevoke(sessionId) {
      this.retryLoginWithParams({ revoke_session_id: sessionId });
    },
    handleSessionRevokeAll() {
      this.retryLoginWithParams({ revoke_all_sessions: true });
    },
    handleSessionLimitCancel() {
      this.sessionsLimitReached = false;
      this.limitedSessions = [];
      this.credentials.password = '';
    },
  },
};
</script>

<template>
  <AuthLayout>
    <template v-if="showSignupLink" #header>
      {{ $t('TALKROBIN_AUTH.LOGIN.NEW_HERE') }}
      <router-link
        to="/app/auth/signup"
        class="ml-1 font-semibold text-robin-indigo hover:text-robin-indigo-light dark:text-robin-indigo-light"
      >
        {{ $t('TALKROBIN_AUTH.LOGIN.START_FREE') }}
      </router-link>
    </template>

    <SessionLimitOverlay
      v-if="sessionsLimitReached"
      :sessions="limitedSessions"
      @revoke="handleSessionRevoke"
      @revoke-all="handleSessionRevokeAll"
      @cancel="handleSessionLimitCancel"
    />

    <MfaVerification
      v-else-if="mfaRequired"
      :mfa-token="mfaToken"
      :verification-channel="verificationChannel"
      @verified="handleMfaVerified"
      @cancel="handleMfaCancel"
    />

    <div v-else-if="email" class="flex items-center justify-center py-20">
      <Spinner color-scheme="primary" size="" />
    </div>

    <section v-else :class="{ 'animate-wiggle': loginApi.hasErrored }">
      <AuthHeading
        :kicker="$t('TALKROBIN_AUTH.LOGIN.KICKER')"
        :title="$t('TALKROBIN_AUTH.LOGIN.TITLE')"
        :highlight="$t('TALKROBIN_AUTH.LOGIN.HIGHLIGHT')"
        :description="$t('TALKROBIN_AUTH.LOGIN.DESCRIPTION')"
      />

      <div v-if="showGoogleOAuth || showSamlLogin" class="flex flex-col gap-3 mb-6">
        <GoogleOAuthButton v-if="showGoogleOAuth" />
        <router-link
          v-if="showSamlLogin"
          to="/app/login/sso"
          class="inline-flex items-center justify-center w-full h-12 gap-2 text-sm font-semibold transition-colors border rounded-xl border-[#E4E6EB] text-robin-navy hover:border-[#C9CCF5] hover:bg-robin-sky dark:border-white/10 dark:text-white dark:hover:bg-white/5"
        >
          <Icon icon="i-lucide-lock-keyhole" class="size-4" />
          {{ $t('LOGIN.SAML.LABEL') }}
        </router-link>
        <SimpleDivider :label="$t('TALKROBIN_AUTH.LOGIN.OR_EMAIL')" />
      </div>

      <form class="space-y-5" @submit.prevent="submitFormLogin">
        <FormInput
          v-model="credentials.email"
          name="email_address"
          type="text"
          data-testid="email_input"
          autocomplete="email"
          :tabindex="1"
          required
          :label="$t('LOGIN.EMAIL.LABEL')"
          :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
          :has-error="v$.credentials.email.$error"
          @input="v$.credentials.email.$touch"
        />
        <FormInput
          v-model="credentials.password"
          type="password"
          name="password"
          data-testid="password_input"
          autocomplete="current-password"
          required
          :tabindex="2"
          :label="$t('LOGIN.PASSWORD.LABEL')"
          :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
          :has-error="v$.credentials.password.$error"
          @input="v$.credentials.password.$touch"
        >
          <router-link
            v-if="!globalConfig.disableUserProfileUpdate"
            to="/app/auth/reset/password"
            class="text-[13px] font-medium text-robin-indigo hover:text-robin-indigo-light dark:text-robin-indigo-light"
            tabindex="4"
          >
            {{ $t('LOGIN.FORGOT_PASSWORD') }}
          </router-link>
        </FormInput>
        <AuthButton
          data-testid="submit_button"
          class="!mt-7"
          :tabindex="3"
          :label="$t('LOGIN.SUBMIT')"
          :is-loading="loginApi.showLoading"
        />
      </form>
    </section>

    <template #aside>
      <AuthShowcase
        :title="$t('TALKROBIN_AUTH.LOGIN.SHOWCASE_TITLE')"
        :highlight="$t('TALKROBIN_AUTH.LOGIN.SHOWCASE_HIGHLIGHT')"
      />
    </template>
  </AuthLayout>
</template>
